// ============================================================
// HOOKED — ieCam.js   THE CAMERA CONTRACT (shared, persisted)
//
// Dependency-free on purpose. Game logic must NEVER import
// three/fiber/drei to learn which way is forward:
//        logic  →  data  ←  renderer
//
// Four pieces, per ie-3d-conversion's CAMERA CONTRACT. All four
// or none — three of the four is worse than a fixed camera.
//
//   1 TWO VIEWS, SHARED + PERSISTED. A view is a distance
//     MULTIPLIER, never a position, so one setting is legal in
//     the wharf, on the water, and in any room added later.
//   2 FREE POSE. The moment the player drags, the rig stops
//     steering and CARRIES the pose (translate, never re-aim).
//   3 DEFAULT IS AN ACTION, NOT A THIRD VIEW — and it is a
//     COUNTER, so pressing it while already on TRAIL still works.
//   4 MOVEMENT FOLLOWS THE EYE. THE DEFAULT IS THE IDENTITY:
//     forward starts at (0,-1) — exactly what Lake.js assumed
//     before this module existed — so rotateInput() is safe to
//     apply unconditionally, on every screen, forever.
// ============================================================

const KEY = "hooked_cam_view_v1";

export const VIEWS = [
  { id: "follow", label: "FOLLOW", mult: 0.52 },
  { id: "trail",  label: "TRAIL",  mult: 1.0  },
];
const DEFAULT_VIEW = "trail";

// ── state ────────────────────────────────────────────────────
let view = DEFAULT_VIEW;
// Validate the persisted enum against the LIVE list on read, so
// dropping a view later self-heals instead of stranding someone
// in a mode that no longer exists.
try {
  const s = typeof localStorage !== "undefined" && localStorage.getItem(KEY);
  if (s && VIEWS.some((v) => v.id === s)) view = s;
} catch (e) { /* private mode — defaults are fine */ }

let poseRelease = 0;   // ++ whenever the pose must be dropped
let resetCount  = 0;   // ++ on Default. A COUNTER, never a boolean.
let fwdX = 0, fwdZ = -1;   // ← THE IDENTITY

const subs = new Set();
const notify = () => subs.forEach((f) => { try { f(); } catch (e) {} });

// ── views ────────────────────────────────────────────────────
export const getView = () => view;
export const getMult = () => (VIEWS.find((v) => v.id === view) || VIEWS[1]).mult;

export function setView(id) {
  if (!VIEWS.some((v) => v.id === id)) return;
  view = id;
  try { localStorage.setItem(KEY, id); } catch (e) {}
  // Selecting a view MUST release the pose, or FOLLOW appears to
  // do nothing once the camera has been hand-placed, and reads as
  // a bug rather than a mode.
  poseRelease++;
  notify();
}

// ── default ──────────────────────────────────────────────────
export function requestDefault() {
  resetCount++;
  poseRelease++;
  view = DEFAULT_VIEW;
  try { localStorage.setItem(KEY, DEFAULT_VIEW); } catch (e) {}
  notify();
}
export const getResetCount  = () => resetCount;
export const getPoseRelease = () => poseRelease;

// ── the ground-forward vector ────────────────────────────────
export const getForward = () => ({ x: fwdX, z: fwdZ });

export function setForward(x, z) {
  const l = Math.hypot(x, z);
  if (!l || !isFinite(l)) return;
  fwdX = x / l; fwdZ = z / l;
}

// A yaw left over from one screen silently rotating input in
// another is worse than a crash. Rigs RESET on unmount.
export function resetForward() { fwdX = 0; fwdZ = -1; }

/**
 * Rotate raw tile-space input by the camera's ground forward.
 * Input is HOOKED's own convention: dx = +east, dy = +south
 * (so W/Up is dy = -1). Output is the same convention.
 *
 * F = forward, R = F rotated +90° about Y = (-fz, fx).
 * out = F * (-dy) + R * (dx)
 */
export function rotateInput(dx, dy) {
  const f = -dy, r = dx;
  const ox = fwdX * f + -fwdZ * r;
  const oz = fwdZ * f +  fwdX * r;
  return { dx: ox, dy: oz };
}

// ── subscription (for DOM chrome that shows the active view) ──
export function subscribe(fn) { subs.add(fn); return () => subs.delete(fn); }
